import React from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import './SelectImagesStyle.css'
import type { ReportImage } from '../reportTypes'

interface CustomToggleProps {
  children: React.ReactNode
  onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}

// eslint-disable-next-line react/display-name
const CustomToggle = React.forwardRef<HTMLAnchorElement, CustomToggleProps>(
  ({ children, onClick }, ref) => (
    <a
      ref={ref}
      onClick={(e) => {
        e.preventDefault()
        onClick(e)
      }}
      className="si-menu-toggle"
    >
      {children}
    </a>
  ),
)

interface ImageCardProps {
  image: ReportImage
  onToggleSelect: (id: string) => void
  onToggleFavorite: (id: string) => void
}

const ImageCard: React.FC<ImageCardProps> = ({
  image,
  onToggleSelect,
  onToggleFavorite,
}) => {
  const placeholderBg = '#1a1a2e'

  return (
    <div className={`si-card ${image.isSelected ? 'si-card-selected' : ''}`}>
      <div className="si-card-header">
        <div className="si-card-info">
          <span className="si-card-filename">{image.fileName}</span>
          <span className="si-card-date">
            {image.uploadDate} - ({image.fileSize})
          </span>
        </div>
        <Dropdown className="si-card-menu" align="end">
          <Dropdown.Toggle as={CustomToggle}>
            <i className="bi bi-three-dots-vertical" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item>Download</Dropdown.Item>
            <Dropdown.Item>Details</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div
        className="si-card-image-wrapper"
        onClick={() => onToggleSelect(image.id)}
        role="button"
        tabIndex={0}
        aria-label={`Select ${image.fileName}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggleSelect(image.id)
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        {image.imageUrl ? (
          <img
            src={image.imageUrl}
            alt={image.fileName}
            onError={(e) => {
              // Azure rejected the URL (e.g. signing failed upstream and the
              // raw blob URL came through). Hide the broken-image icon and
              // let the placeholder show through instead.
              const el = e.currentTarget as HTMLImageElement
              el.style.display = 'none'
              const wrapper = el.parentElement
              if (wrapper && !wrapper.querySelector('.si-card-placeholder')) {
                const ph = document.createElement('div')
                ph.className = 'si-card-placeholder'
                ph.style.backgroundColor = placeholderBg
                wrapper.insertBefore(ph, wrapper.firstChild)
              }
            }}
          />
        ) : (
          <div
            className="si-card-placeholder"
            style={{ backgroundColor: placeholderBg }}
          />
        )}

        <button
          className={`si-favorite-btn ${image.isFavorite ? 'si-favorite-active' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            onToggleFavorite(image.id)
          }}
          aria-label="Toggle favorite"
        >
          {image.isFavorite ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="#facc15"
              stroke="#facc15"
              strokeWidth="2"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          )}
        </button>

        <Form.Check
          type="checkbox"
          checked={image.isSelected}
          onChange={() => onToggleSelect(image.id)}
          onClick={(e) => e.stopPropagation()}
          className="si-card-checkbox"
        />
      </div>
    </div>
  )
}

interface SelectImagesProps {
  images: ReportImage[]
  onToggleSelect: (id: string) => void
  onToggleFavorite: (id: string) => void
}

const SelectImages: React.FC<SelectImagesProps> = ({
  images,
  onToggleSelect,
  onToggleFavorite,
}) => {
  return (
    <div className="si-grid">
      {images.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          onToggleSelect={onToggleSelect}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </div>
  )
}

export default SelectImages
